import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import type {
  DocRefSourceLocation,
  DocumentEnvelopePayload,
  TimelineEnvelope,
} from '../../types';

import {
  kindIcon,
  kindLabel,
  kindStyle,
  provenanceGlyph,
} from './constants';
import {
  docRefStatusLabel,
  formatDateRange,
  isGeneratedDoc,
} from './helpers';

interface DocRefCardProps {
  envelope: TimelineEnvelope;
  /** When the card is rendered as a thread child, callers can collapse the
   *  redundant facility/provider line that the encounter root already shows. */
  inThread?: boolean;
  /** Receives the full envelope so the parent can route DocRefs to the
   *  drawer and generated docs to a direct download. */
  onOpen?: (envelope: TimelineEnvelope) => void;
}


const TERMINAL_DOCREF = new Set(['enriched', 'failed']);

/**
 * One DocumentReference card in the patient timeline. Drives off the
 * envelope's `kind: "document"` payload, which carries the clinical
 * fields (`title`, `description`, `doc_kind`, `authored_at`, etc.).
 *
 * Visual structure mirrors the prior ArtifactCard — kind badge w/
 * provenance glyph, meta strip, description, optional sections collapse
 * — but the data model has changed: cards now describe a clinical record
 * (DocRef), not a raw upload.
 */
export function DocRefCard({ envelope, inThread = false, onOpen }: DocRefCardProps) {
  const payload = (envelope.payload ?? {}) as DocumentEnvelopePayload;
  const [outlineOpen, setOutlineOpen] = useState(false);

  const status = payload.processing_status ?? 'enriched';
  const generated = isGeneratedDoc(envelope);
  const isFailed = status === 'failed';
  // Generated documents (lab orders, form PDFs, markdown notes) report
  // `pending` when LLM enrichment hasn't run, but the file itself is
  // already downloadable — there's no "reading" happening. Treat them as
  // terminal so we don't lie to the user with a skeleton + "Reading…"
  // label. Only honor `pending` for real DocRefs whose enrichment is
  // genuinely in flight.
  const isTerminal = generated || TERMINAL_DOCREF.has(status);
  const isPending = !isTerminal;

  const kind = payload.doc_kind ?? envelope.subkind ?? 'other';
  const KindIcon = kindIcon(kind);
  const color = kindStyle(kind);
  const label = kindLabel(kind);
  const glyph = provenanceGlyph(envelope.extracted_by);

  const title =
    payload.title || `Document ${envelope.id.slice(0, 8)}`;

  const handleOpen = () => onOpen?.(envelope);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: 'divider',
        opacity: isPending ? 0.85 : 1,
        transition: 'opacity 200ms',
      }}
    >
      <Stack direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
        {/* Kind badge with provenance glyph */}
        <Box
          sx={{
            position: 'relative',
            width: 32,
            height: 32,
            borderRadius: 1,
            backgroundColor: color.bg,
            color: color.fg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-hidden
        >
          <KindIcon sx={{ fontSize: 18 }} />
          <Box
            component="span"
            sx={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              fontSize: 9,
              lineHeight: 1,
              color: color.fg,
              backgroundColor: 'background.paper',
              borderRadius: '50%',
              width: 12,
              height: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {glyph}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, lineHeight: 1.3 }}
            noWrap
          >
            {title}
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            divider={<MetaDot />}
            sx={{alignItems: "center",  mt: 0.25, color: 'text.secondary' }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, color: color.fg }}>
              {label}
            </Typography>
            {!inThread && payload.author_facility && (
              <Typography variant="caption">{payload.author_facility}</Typography>
            )}
            {payload.authored_at && (
              <Typography variant="caption">
                {formatDateRange(payload.authored_at, payload.authored_at)}
              </Typography>
            )}
            {payload.page_count != null && (
              <Typography variant="caption">{payload.page_count} pp</Typography>
            )}
            <SourceLocationCaption location={payload.source_location ?? null} />
          </Stack>
        </Box>
      </Stack>

      {/* Body */}
      <Box sx={{ mt: 1, pl: 5.25 }}>
        {isPending ? (
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {docRefStatusLabel(status)}
            </Typography>
            <Skeleton variant="text"  sx={{ width: "80%", height: 16 }}/>
            <Skeleton variant="text"  sx={{ width: "60%", height: 16 }}/>
          </Stack>
        ) : isFailed ? (
          <Typography variant="body2" color="error">
            Processing failed. You can still open the original file.
          </Typography>
        ) : payload.description ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {payload.description}
          </Typography>
        ) : null}
      </Box>

      {/* Section outline placeholder — the timeline payload doesn't carry
          sections inline (those live on the DocRef detail). The drawer
          loads them on demand. Generated documents have no sections /
          enrichment, so we hide this affordance for them entirely. */}
      {isTerminal && !isFailed && payload.description && !generated && (
        <Box sx={{ mt: 1, pl: 5.25 }}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setOutlineOpen(o => !o)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOutlineOpen(o => !o);
              }
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              cursor: 'pointer',
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                transform: outlineOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 150ms',
              }}
            />
            <Typography variant="caption">
              {outlineOpen ? 'Hide details' : 'Show details'}
            </Typography>
          </Box>
        </Box>
      )}

      {isTerminal && !isFailed && (
        <Box sx={{ mt: 1, pl: 5.25 }}>
          <Button
            size="small"
            variant="text"
            onClick={handleOpen}
            sx={{ textTransform: 'none', fontWeight: 600, px: 0 }}
          >
            Open →
          </Button>
        </Box>
      )}
    </Paper>
  );
}

function SourceLocationCaption({
  location,
}: {
  location: DocRefSourceLocation | null;
}) {
  if (!location) return null;
  const start = location.page_start;
  const end = location.page_end;
  if (start == null) return null;
  const range = end != null && end !== start ? `Pages ${start}–${end}` : `Page ${start}`;
  return (
    <Typography
      variant="caption"
      color="text.disabled"
      sx={{ fontStyle: 'italic' }}
    >
      {range} of source
    </Typography>
  );
}

function MetaDot() {
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{ color: 'text.disabled', mx: 0.5 }}
      aria-hidden
    >
      ·
    </Typography>
  );
}
